import { Popover, Portal, Transition } from "@headlessui/react";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { usePopper } from "react-popper";

import { arrowStyle, buttonStyle, panelStyle } from "./index.styles";

import { DebouncedTaskRunner } from "~/client/utils/DebouncedTaskRunner";

interface Props {
  children: ReactNode;
  panel: ReactNode;
  width?: string;
  onBeforeShow?: () => Promise<void>;
}

export const HoverPopover: React.FC<Props> = ({ children, panel, width = "280px", onBeforeShow }) => {
  const [showRunner] = useState(new DebouncedTaskRunner(400));
  const [closeRunner] = useState(new DebouncedTaskRunner(400));
  const [isShowing, setIsShowing] = useState(false);
  const [referenceElement, setReferenceElement] = useState();
  const [popperElement, setPopperElement] = useState();
  const arrow = useRef<HTMLDivElement>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "top",
    modifiers: [
      { name: "arrow", options: { element: arrow.current } },
      { name: "offset", options: { offset: [0, 8] } },
    ],
  });

  const handleMouseEnter = (isButton = false) => {
    closeRunner.clear();
    showRunner.enqueue(async () => {
      if (isButton && onBeforeShow) {
        await onBeforeShow();
      }
      setIsShowing(true);
    });
  };

  const handleMouseLeave = () => {
    showRunner.clear();
    closeRunner.enqueue(() => setIsShowing(false));
  };

  return (
    <Popover onMouseLeave={handleMouseLeave}>
      <Popover.Button
        as="div"
        ref={setReferenceElement as any}
        css={buttonStyle}
        onMouseEnter={() => handleMouseEnter(true)}
      >
        {children}
      </Popover.Button>

      {/** @ts-expect-error: `Expression produces a union type that is too complex to represent.`エラーを予測。Headless UIでもこれの解決策を模索しているとのこと。参考: https://github.com/tailwindlabs/headlessui/issues/1523 */}
      <Transition show={isShowing}>
        <Portal>
          <Popover.Panel
            ref={setPopperElement as any}
            style={styles.popper}
            css={panelStyle({ width })}
            onMouseEnter={() => handleMouseEnter(true)}
            {...attributes.popper}
          >
            <>
              {panel}
              <div style={styles.arrow} {...attributes.arrow} ref={arrow} css={arrowStyle} id="popover-arrow" />
            </>
          </Popover.Panel>
        </Portal>
      </Transition>
    </Popover>
  );
};
